import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { apiError } from "../lib/api";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "../components/LanguageSwitcher";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success(t("login.welcome"));
      navigate("/");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }

  function loginWithGoogle() {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left visual */}
      <div className="relative hidden lg:block bg-cover bg-center grain-overlay"
        style={{ backgroundImage: "url(https://images.pexels.com/photos/13551576/pexels-photo-13551576.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200)" }}>
        <div className="absolute inset-0 bg-gradient-to-br from-[#1C1917]/40 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12 text-white fade-in-up">
          <p className="text-xs uppercase tracking-[0.35em] mb-4 opacity-80">{t("login.heroEyebrow")}</p>
          <h1 className="font-heading text-5xl xl:text-6xl font-light leading-[1.05] max-w-md">{t("login.heroTitle")}</h1>
          <p className="mt-6 text-sm font-light opacity-80 max-w-sm">{t("login.heroSub")}</p>
        </div>
      </div>
      {/* Right form */}
      <div className="flex flex-col justify-center px-8 sm:px-16 lg:px-24 py-16 bg-[#FAF9F6] relative">
        <div className="absolute top-6 right-6"><LanguageSwitcher /></div>
        <div className="max-w-md w-full mx-auto fade-in-up">
          <div className="mb-10">
            <p className="text-xs uppercase tracking-[0.3em] text-stone-500 mb-3">{t("login.eyebrow")}</p>
            <h2 className="font-heading text-4xl font-light text-stone-900">{t("login.title")}</h2>
          </div>

          <button
            type="button"
            onClick={loginWithGoogle}
            data-testid="login-google"
            className="w-full flex items-center justify-center gap-3 py-3 rounded-full border border-stone-300 bg-white hover:bg-stone-50 transition-colors duration-300 mb-5"
          >
            <svg width="18" height="18" viewBox="0 0 24 24">
              <path fill="#EA4335" d="M12 10.2v3.9h5.5c-.24 1.4-1.7 4.1-5.5 4.1-3.31 0-6-2.74-6-6.1s2.69-6.1 6-6.1c1.88 0 3.15.8 3.87 1.49l2.64-2.55C16.9 3.4 14.7 2.4 12 2.4 6.86 2.4 2.7 6.56 2.7 12s4.16 9.6 9.3 9.6c5.37 0 8.93-3.77 8.93-9.08 0-.6-.07-1.07-.16-1.52H12z"/>
            </svg>
            <span className="text-sm text-stone-800">{t("login.googleBtn")}</span>
          </button>

          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-stone-200" />
            <span className="text-[11px] uppercase tracking-widest text-stone-400">{t("login.divider")}</span>
            <div className="flex-1 h-px bg-stone-200" />
          </div>

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2 block">{t("login.email")}</label>
              <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required
                autoComplete="email"
                data-testid="login-email"
                className="w-full px-4 py-3 bg-white border border-stone-200 rounded-xl focus:outline-none focus:border-[#D4A373] focus:ring-1 focus:ring-[#D4A373] transition-all" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2 block">{t("login.password")}</label>
              <input value={password} onChange={(e) => setPassword(e.target.value)} type="password" required
                autoComplete="current-password"
                data-testid="login-password"
                className="w-full px-4 py-3 bg-white border border-stone-200 rounded-xl focus:outline-none focus:border-[#D4A373] focus:ring-1 focus:ring-[#D4A373] transition-all" />
            </div>
            <button type="submit" disabled={loading} data-testid="login-submit"
              className="w-full py-3 rounded-full bg-[#1C1917] text-white hover:bg-stone-800 transition-colors duration-300 font-light tracking-wide disabled:opacity-50">
              {loading ? t("login.submitting") : t("login.submit")}
            </button>
          </form>
          <p className="text-sm text-stone-500 mt-6">
            {t("login.noAccount")} <Link to="/registro" className="text-stone-900 underline underline-offset-4" data-testid="link-register">{t("login.register")}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
